/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Camera, ScanLine, Upload, Loader2, Sparkles, AlertTriangle, CheckCircle2, X, FileImage,
} from 'lucide-react';
import { scanPrescription, fileToBase64 } from '@/lib/sprint2';
import { cn } from '@/lib/utils';

const MAX_MB = 6;

/**
 * PrescriptionOCRDialog — upload a photo of a paper prescription, Claude 3.5 Vision
 * extracts medications and returns structured data. Parent supplies `onExtracted(meds)`
 * to receive the parsed rows and pre-fill their own prescription form.
 */
const PrescriptionOCRDialog = ({ open, onOpenChange, patientContext, onExtracted }) => {
  const [preview, setPreview] = useState(null);   // data URL
  const [base64, setBase64] = useState(null);
  const [mime, setMime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);     // parsed JSON from backend
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const reset = () => {
    setPreview(null); setBase64(null); setMime(null);
    setLoading(false); setResult(null); setError(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const maxBytes = MAX_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`Immagine troppo grande (max ${MAX_MB} MB)`);
      return;
    }
    try {
      const { base64: b64, dataUrl, mime: m } = await fileToBase64(file);
      setBase64(b64);
      setPreview(dataUrl);
      setMime(m || 'image/jpeg');
      setResult(null);
      setError(null);
    } catch (err) {
      toast.error('Impossibile leggere il file');
    }
  };

  const scan = async () => {
    if (!base64) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const res = await scanPrescription({
        image_base64: base64,
        mime_type: mime,
        patient_context: patientContext || undefined,
      });
      setResult(res.result);
      const meds = res?.result?.medications || [];
      if (meds.length === 0) {
        toast.warning('Nessun farmaco riconosciuto — controlla l\'immagine.');
      } else {
        toast.success(`${meds.length} farmac${meds.length === 1 ? 'o' : 'i'} riconosciut${meds.length === 1 ? 'o' : 'i'}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore durante l\'analisi AI';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const applyToPrescription = () => {
    const meds = (result?.medications || []).map((m) => ({
      name: m.drug_name || m.active_ingredient || '',
      dosage: m.dosage || '',
      frequency: m.frequency || '',
      duration: m.duration || '',
      notes: [m.form, m.notes].filter(Boolean).join(' — '),
    })).filter((m) => m.name);
    if (meds.length === 0) {
      toast.error('Nessun farmaco valido da importare');
      return;
    }
    onExtracted?.(meds, result);
    onOpenChange?.(false);
    setTimeout(reset, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange?.(o); if (!o) setTimeout(reset, 300); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="ocr-prescription-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" /> Scansiona ricetta cartacea
          </DialogTitle>
          <DialogDescription>
            Carica una foto della ricetta: l'AI (Claude 3.5 Vision) estrae automaticamente farmaci e dosaggi.
          </DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-3">
            <Card
              className="p-6 border-dashed border-2 flex flex-col items-center gap-3 cursor-pointer hover:border-primary/60 transition-colors"
              onClick={() => inputRef.current?.click()}
              data-testid="ocr-dropzone"
            >
              <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                <FileImage className="h-6 w-6 text-primary" />
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold">Clicca qui per caricare un'immagine</div>
                <div className="text-xs text-muted-foreground mt-1">PNG, JPEG, WEBP · max {MAX_MB} MB</div>
              </div>
            </Card>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => inputRef.current?.click()} data-testid="ocr-upload-btn">
                <Upload className="h-4 w-4" /> Galleria
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => cameraRef.current?.click()} data-testid="ocr-camera-btn">
                <Camera className="h-4 w-4" /> Fotocamera
              </Button>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="ocr-file-input"
            />
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="ocr-camera-input"
            />
          </div>
        )}

        {preview && (
          <div className="space-y-4">
            <div className="relative">
              <img src={preview} alt="Ricetta" className="max-h-80 w-full object-contain rounded-xl border border-border" data-testid="ocr-preview-img" />
              <Button
                variant="secondary"
                size="icon"
                className="absolute top-2 right-2"
                onClick={reset}
                data-testid="ocr-clear-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {!result && !loading && (
              <Button onClick={scan} className="w-full" size="lg" data-testid="ocr-scan-btn">
                <Sparkles className="h-4 w-4" /> Analizza con AI
              </Button>
            )}
            {loading && (
              <div className="flex items-center justify-center gap-2 py-4 text-primary">
                <Loader2 className="h-4 w-4 animate-spin" /> Analisi AI in corso…
              </div>
            )}
            {error && !loading && (
              <div className="rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm p-3" data-testid="ocr-error">
                {error}
              </div>
            )}

            {result && (
              <div className="space-y-3" data-testid="ocr-result-panel">
                {result.warnings?.length > 0 && (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs p-2 flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold mb-0.5">Avvertimenti:</div>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {result.warnings.map((w, i) => <li key={`w-${i}-${(w || '').slice(0, 20)}`}>{w}</li>)}
                      </ul>
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">
                    Farmaci riconosciuti ({(result.medications || []).length})
                  </div>
                  {(result.medications || []).length === 0 ? (
                    <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                      Nessun farmaco identificato nell'immagine.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {result.medications.map((m, idx) => (
                        <Card key={`med-${idx}-${(m.drug_name || 'x').slice(0, 12)}`} className="p-3" data-testid={`ocr-med-row-${idx}`}>
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="font-semibold text-sm">{m.drug_name || 'Farmaco'}</div>
                            <Badge variant="outline" className={cn(
                              'text-[10px]',
                              (m.confidence || 0) >= 0.75 ? 'border-emerald-300 bg-emerald-50 text-emerald-800' :
                              (m.confidence || 0) >= 0.5 ? 'border-amber-300 bg-amber-50 text-amber-800' :
                              'border-red-300 bg-red-50 text-red-800'
                            )}>
                              {Math.round(((m.confidence || 0) * 100))}% conf.
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            {m.active_ingredient && <div><span className="font-semibold text-foreground">Principio:</span> {m.active_ingredient}</div>}
                            {m.dosage && <div><span className="font-semibold text-foreground">Dose:</span> {m.dosage}</div>}
                            {m.frequency && <div><span className="font-semibold text-foreground">Frequenza:</span> {m.frequency}</div>}
                            {m.duration && <div><span className="font-semibold text-foreground">Durata:</span> {m.duration}</div>}
                            {m.form && <div><span className="font-semibold text-foreground">Forma:</span> {m.form}</div>}
                            {m.notes && <div className="col-span-2"><span className="font-semibold text-foreground">Note:</span> {m.notes}</div>}
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>

                {(result.doctor_name || result.issue_date || result.diagnosis_hint) && (
                  <div className="rounded-lg border p-3 text-xs space-y-1">
                    {result.doctor_name && <div><span className="font-semibold">Medico:</span> {result.doctor_name}</div>}
                    {result.issue_date && <div><span className="font-semibold">Data:</span> {result.issue_date}</div>}
                    {result.diagnosis_hint && <div><span className="font-semibold">Diagnosi/ICD:</span> {result.diagnosis_hint}</div>}
                  </div>
                )}

                <div className="text-[11px] italic text-muted-foreground border-t border-border/40 pt-2">
                  {result.disclaimer || 'Estrazione AI: verificare sempre prima di dispensare/prescrivere farmaci.'}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)}>
            Annulla
          </Button>
          {result && (result.medications || []).length > 0 && (
            <Button onClick={applyToPrescription} data-testid="ocr-apply-btn">
              <CheckCircle2 className="h-4 w-4" /> Importa nella prescrizione
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrescriptionOCRDialog;
